import React, { useEffect, useState } from "react";
import { Clock, DollarSign, ShoppingCart } from "lucide-react";

const fields = [
  {
    key: "forecast_ca_eur",
    label: "CA production",
    description: "Montant retenu pour le pilotage de production.",
    icon: DollarSign,
    suffix: "EUR",
    step: "100",
  },
  {
    key: "forecast_time_hours",
    label: "Budget heures",
    description: "Charge retenue pour l'iteration, en heures.",
    icon: Clock,
    suffix: "h",
    step: "1",
  },
  {
    key: "forecast_purchase_eur",
    label: "Budget achats",
    description: "Budget achats retenu pour l'iteration.",
    icon: ShoppingCart,
    suffix: "EUR",
    step: "100",
  },
];

const IterationForecastPanel = ({ iteration, onInlineUpdate }) => {
  const [draft, setDraft] = useState({
    forecast_ca_eur: "",
    forecast_time_hours: "",
    forecast_purchase_eur: "",
  });
  const [savingField, setSavingField] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setDraft({
      forecast_ca_eur:
        iteration?.forecast_ca_eur !== null && iteration?.forecast_ca_eur !== undefined
          ? String(iteration.forecast_ca_eur)
          : "",
      forecast_time_hours:
        iteration?.forecast_time_hours !== null && iteration?.forecast_time_hours !== undefined
          ? String(iteration.forecast_time_hours)
          : "",
      forecast_purchase_eur:
        iteration?.forecast_purchase_eur !== null && iteration?.forecast_purchase_eur !== undefined
          ? String(iteration.forecast_purchase_eur)
          : "",
    });
  }, [iteration?.forecast_ca_eur, iteration?.forecast_time_hours, iteration?.forecast_purchase_eur]);

  const handleChange = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const commitField = async (field) => {
    if (!iteration || !onInlineUpdate) return;
    const rawValue = draft[field];
    const trimmed = typeof rawValue === "string" ? rawValue.trim() : rawValue;

    if (trimmed === "") {
      setSavingField(field);
      setError(null);
      try {
        await onInlineUpdate(iteration.id, field, null);
      } catch (e) {
        setError(e.message || "Impossible de mettre a jour la valeur.");
      } finally {
        setSavingField(null);
      }
      return;
    }

    const numericValue = Number(trimmed);
    if (Number.isNaN(numericValue)) {
      setError("Saisissez un nombre valide.");
      return;
    }

    setSavingField(field);
    setError(null);
    try {
      await onInlineUpdate(iteration.id, field, numericValue);
    } catch (e) {
      setError(e.message || "Impossible de mettre a jour la valeur.");
    } finally {
      setSavingField(null);
    }
  };

  const handleKeyDown = (field, event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitField(field);
    }
  };

  if (!iteration) return null;

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
      <div>
        <p className="text-sm font-semibold text-slate-900">Budgets de production</p>
        <p className="text-xs text-slate-500">
          Ces valeurs alimentent la charge et le CA de production.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {error}
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-3">
        {fields.map((field) => {
          const Icon = field.icon;
          return (
            <label
              key={field.key}
              className="flex flex-col rounded-lg border border-slate-100 bg-slate-50 p-2.5 text-sm text-slate-600"
            >
              <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <Icon className="h-4 w-4 text-blue-500" />
                {field.label}
              </span>
              <input
                type="number"
                step={field.step}
                value={draft[field.key]}
                onChange={(e) => handleChange(field.key, e.target.value)}
                onBlur={() => commitField(field.key)}
                onKeyDown={(event) => handleKeyDown(field.key, event)}
                className="mt-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder={field.suffix}
              />
              <span className="mt-1 text-[11px] text-slate-500">{field.description}</span>
              {savingField === field.key && (
                <span className="mt-1 text-[11px] text-blue-500">Mise a jour...</span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default IterationForecastPanel;
